import {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {Box} from '@jobseeker/lotus/foundations';
import {whenIdle} from '@jobseeker/common-core/utils/externalScript';
import {
  loadMyChatSdk,
  loadStickyChatSdk,
} from '@jobseeker/common-core/utils/recruiterChat';
import {COOKIE_CKT} from '@constants';

import type {AppState} from '@jobseeker/common-core/types';
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

// The SDK mounts into #sticky-chat once per session, matching the old inline
// <Script id> which next/script never re-ran after the first mount.
let stickyChatInitialized = false;

export const Chat = () => {
  const isAboveLg = useIsLargeDevice('lg');
  const isAboveXL = useIsLargeDevice(1442);
  const [chatRight, setChatRight] = useState(0);

  const {
    userId,
    authInfo: {token},
  } = useSelector((state: AppState) => state.auth);

  useEffect(() => {
    let rafId: number | null = null;

    const calculateChatRight = () => {
      const adsBanner = document.getElementById(
        'job_ads_banner_featured-institute'
      );
      const boundingBox = adsBanner?.getBoundingClientRect();
      if (boundingBox) {
        const windowWidth = window.innerWidth;
        const offsetRight = windowWidth - boundingBox?.right;
        setChatRight(offsetRight);
      }
    };

    // Coalesce resize bursts into one rAF so the layout read runs once per
    // frame at a clean point rather than reflowing on every resize event.
    const onResize = () => {
      if (rafId === null) {
        rafId = window.requestAnimationFrame(() => {
          rafId = null;
          calculateChatRight();
        });
      }
    };

    calculateChatRight();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, []);

  const offsetRight = isAboveXL
    ? isAppIimjobs
      ? chatRight
      : 0
    : isAboveLg
    ? chatRight
    : 0;
  const offsetRightRef = useRef(offsetRight);
  offsetRightRef.current = offsetRight;

  useEffect(() => {
    if (!userId || !token || stickyChatInitialized) return;
    let cancelled = false;

    whenIdle()
      .then(loadStickyChatSdk)
      .then(chat => {
        if (cancelled || stickyChatInitialized || !chat?.stickyChat) return;
        stickyChatInitialized = true;
        chat.stickyChat({
          theme: 'seeker-theme-one',
          userId: Number(userId),
          userType: 'j',
          token,
          tokenName: `${process.env.NEXT_PUBLIC_APP_NAME?.toUpperCase()}${COOKIE_CKT}`,
          position: 'sticky', //For jobseeker sticky chat
          offsetRight: offsetRightRef.current,
          // Behaviour-neutral no-op. The old inline script stringified a closure
          // over `dispatch`, so it threw (minified name undefined globally) and
          // was swallowed on every call. It also targeted the wrong action
          // (set_naviagtion_location). The SDK only passes the unread count and
          // updates the tab title/sound itself. Wiring this to
          // AppContext/set_chat_count (header avatar dot) is a separate change.
          notifyMessage: () => undefined,
        });
      })
      .catch(error => console.error('[sticky-chat] SDK load failed:', error));

    return () => {
      cancelled = true;
    };
  }, [userId, token]);

  return (
    <>
      {userId && token && (
        <Box id="sticky-chat" display={{xxs: 'none', md: 'block'}} />
      )}
    </>
  );
};

export default Chat;

/**
 * @returns Chat widget component shown on /chats page
 */
export const Chats = () => {
  const {
    userId,
    authInfo: {token},
  } = useSelector((state: AppState) => state.auth);

  const {profile} = useSelector((state: AppState) => state.user);
  const canChat = Boolean(userId && token && profile?.email);
  const name = profile?.name;
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!canChat || initializedRef.current) return;
    let cancelled = false;

    // /chats is the widget's own page, so load right away rather than at idle.
    loadMyChatSdk()
      .then(chat => {
        if (cancelled || initializedRef.current || !chat?.myChat) return;
        initializedRef.current = true;
        chat.myChat({
          userId: Number(userId),
          theme: 'seeker-theme-one',
          userType: 'j',
          token,
          name: name ?? 'Hey there!',
          tokenName: '',
        });
      })
      .catch(error => console.error('[my-chat] SDK load failed:', error));

    return () => {
      cancelled = true;
    };
  }, [canChat, userId, token, name]);

  return canChat ? (
    <Box id="my-chat" sx={{display: 'flex', justifyContent: 'center'}} />
  ) : null;
};
